/**
 * Opening the authoring form on a question that already exists.
 *
 * The preview panel can read a question but not change one, and the only place
 * that can is the composer — a full page. So the pencil in the panel hands the
 * composer a seed: the same fields the blank form starts with, filled from the
 * version the author was looking at.
 *
 * **Why the version and not the row.** The panel lets an author select an
 * older revision, and the pencil edits what is on screen. Seeding from the row
 * would silently swap the wording back to current the moment they clicked, and
 * "start from the version before we broke it" is one of the two reasons the
 * history is selectable at all.
 *
 * **Lossy on purpose.** The pool row is a grid row: it knows the type, topic,
 * difficulty and points, and the preview derives the choices. It has never
 * held rubric wording, references, or tags unless the version already has
 * them: a passage stays on the compose page so the author is not editing
 * a stripped copy of the item they just opened. The hotspot figure lives
 * on the answer canvas, not in References.
 */

import { toFormStatus } from "@/lib/question-status"
import {
  createAuthoringReference,
  type AuthoringQuestionType,
  type AuthoringReference,
  type AuthoringStatus,
  type LeoQuestionDraftPatch,
} from "@/lib/library-authoring"
import type { QuestionBankStatus } from "@/lib/question-bank-status"
import { composerTypeFor, labelForComposerType } from "@/lib/question-type"
import { categoryIdsFromTopic } from "@/lib/blueprint/mapping"
import {
  bankQuestionFolderIds,
  type AssessmentBankQuestion,
} from "@/lib/mock/assessment-add-questions"
import type {
  QuestionAnswerModel,
  QuestionRevision,
  QuestionVersion,
} from "@/lib/question-preview"
import {
  SHOWCASE_PACEMAKER_PASSAGE,
  showcaseItemRationale,
  showcaseStemHasPassage,
} from "@/lib/question-preview"

/** Everything the composer needs to open on an existing question. */
export interface QuestionAuthoringSeed {
  /** The question being revised. Saving writes a new version of this id. */
  questionId: string
  /** Short name, for the composer's "Editing …" chrome and the status line. */
  questionTitle: string
  /** Which revision the author started from. */
  versionNumber: number
  type: AuthoringQuestionType
  folderId: string
  /** All filing locations when the question is multi-filed. */
  folderIds?: string[]
  /**
   * The form fields themselves. Same shape Leo's drafts use, because it is the
   * same job: hand the builder a set of values instead of a blank page. Minus
   * the three curriculum fields, which the composer's schema does not hold and
   * which would otherwise ride into form state as keys nothing reads.
   */
  values: QuestionAuthoringSeedValues
}

/** A published bank question is the editor's Published toggle. Draft stays draft. */
export function authoringStatusForBankStatus(
  status?: QuestionBankStatus | string | null,
): AuthoringStatus {
  return toFormStatus(status)
}

export type QuestionAuthoringSeedValues = Omit<
  LeoQuestionDraftPatch,
  "subjectArea" | "track" | "phase"
> & {
  status?: AuthoringStatus
  categoryIds?: string[]
  classifications?: Record<string, string>
  hotspotImageName?: string
  hotspotImageUrl?: string
  hotspotRegions?: {
    id: string
    shape: "rect" | "circle"
    label: string
    isCorrect: boolean
    x: number
    y: number
    w: number
    h: number
  }[]
}

/**
 * Pool vocabulary to composer vocabulary, and back.
 *
 * The pool names types the way a faculty member says them; the composer names
 * them the way the schema stores them. Both directions now come from
 * `lib/question-type`, so the round-trip is closed: a Multiple Select
 * question edited without touching its format comes back as Multiple Select,
 * and the two composer shades the bank has no word for (Numeric, Ordering)
 * come back as the type they were filed as rather than as an invented one.
 */
export function authoringTypeForPoolType(type: string): AuthoringQuestionType {
  return composerTypeFor(type)
}

export function poolTypeForAuthoringType(type: AuthoringQuestionType): string {
  return labelForComposerType(type)
}

const PAPER_BLOOM_FROM_AUTHORING: Record<string, string> = {
  Remember: "Remembering",
  Remembering: "Remembering",
  Understand: "Understanding",
  Understanding: "Understanding",
  Apply: "Applying",
  Applying: "Applying",
  Analyze: "Analyzing",
  Analyzing: "Analyzing",
  Evaluate: "Evaluating",
  Evaluating: "Evaluating",
  Create: "Creating",
  Creating: "Creating",
}

export function paperBloomFromAuthoring(bloom: string): string {
  const trimmed = bloom.trim()
  return PAPER_BLOOM_FROM_AUTHORING[trimmed] ?? trimmed
}

export function paperDifficultyFromAuthoring(difficulty: string): string {
  if (difficulty === "easy") return "Easy"
  if (difficulty === "medium") return "Medium"
  if (difficulty === "hard") return "Hard"
  return difficulty
}

/** Composer fields in the vocabulary the section blueprint checks. */
export function composerBlueprintSubject(input: {
  topic?: string
  bloom?: string
  type?: AuthoringQuestionType
  difficulty?: string
}): {
  topic: string
  blooms: string
  type: string
  difficulty: string
} {
  return {
    topic: input.topic?.trim() ?? "",
    blooms: paperBloomFromAuthoring(input.bloom ?? ""),
    type: input.type ? poolTypeForAuthoringType(input.type) : "",
    difficulty: paperDifficultyFromAuthoring(input.difficulty ?? ""),
  }
}

const AUTHORING_DIFFICULTY: Record<
  AssessmentBankQuestion["difficulty"],
  "easy" | "medium" | "hard"
> = {
  Easy: "easy",
  Medium: "medium",
  Hard: "hard",
}

function passageReference(questionId: string, html: string): AuthoringReference {
  return {
    ...createAuthoringReference(`ref-${questionId}-passage`, "citation"),
    citation: html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
  }
}

function seededReferences(
  questionId: string,
  stem: string,
): AuthoringReference[] {
  if (!showcaseStemHasPassage(stem) && !/read the following passage/i.test(stem)) {
    return []
  }
  return [passageReference(questionId, SHOWCASE_PACEMAKER_PASSAGE)]
}

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"]

export function questionAuthoringSeed(
  row: AssessmentBankQuestion,
  version: QuestionVersion
): QuestionAuthoringSeed {
  const values: QuestionAuthoringSeedValues = {
    title: row.title,
    leadIn: version.stem,
    difficulty: AUTHORING_DIFFICULTY[row.difficulty],
    points: String(row.points ?? 1),
    bloom: row.bloom ?? "",
    categoryIds: row.categoryUnassigned
      ? []
      : row.categoryIds?.length
        ? [...row.categoryIds]
        : categoryIdsFromTopic(row.topic),
    status: authoringStatusForBankStatus(row.status),
    ...(row.classifications ? { classifications: { ...row.classifications } } : {}),
  }

  if (version.answer.kind === "choices") {
    const carried = Array.isArray(row.carriedChoices) ? row.carriedChoices : null
    const useCarried = Boolean(version.current && carried && carried.length > 0)
    if (version.current && row.stem) values.leadIn = row.stem
    values.options = useCarried
      ? carried!.map((choice, index) => ({
          id: `opt-${row.id}-${LETTERS[index] ?? index + 1}`,
          text: typeof choice.text === "string" ? choice.text : "",
          isCorrect: Boolean(
            (choice as { isCorrect?: boolean; correct?: boolean }).isCorrect ??
              (choice as { correct?: boolean }).correct,
          ),
          rationale: "",
        }))
      : version.answer.choices.map((choice) => ({
      // Keyed by letter so re-opening the same question twice produces the
      // same ids: react-hook-form keys its field array on them, and a fresh
      // id per mount would remount every option row.
      id: `opt-${row.id}-${choice.letter}`,
      text: choice.text,
      isCorrect: choice.correct,
      rationale: "",
    }))
  } else if (version.answer.kind === "open") {
    // Open items have no key to seed. What they do have is the sentence the
    // grader is marked against, and that is what the composer calls the
    // rationale for essays and short answers.
    values.rationale = version.answer.guidance
  } else if (version.answer.kind === "fill_blank") {
    values.leadIn = version.stem.replace(/_{3,}/g, "{{blank}}")
    values.fillBlankAnswers = version.answer.blanks.map((blank, index) => ({
      id: `blk-${row.id}-${index + 1}`,
      // Composer stores variants on one blank as a comma-separated Accepted list.
      accepted: blank.accepted.map((value) => value.trim()).filter(Boolean).join(", "),
    }))
  } else if (version.answer.kind === "matching") {
    values.pairs = version.answer.pairs.map((pair, index) => ({
      id: `pair-${row.id}-${index + 1}`,
      left: pair.prompt,
      right: pair.match,
    }))
  } else if (version.answer.kind === "hotspot") {
    const answer = version.answer
    values.hotspotImageName = answer.imageLabel
    values.hotspotImageUrl = answer.imageSrc
    values.hotspotRegions = answer.regions
      .filter((region) => region.correct)
      .map((region) => ({
        id: region.id,
        shape: "rect" as const,
        label: region.label,
        isCorrect: true,
        x: answer.correctArea.left,
        y: answer.correctArea.top,
        w: answer.correctArea.width,
        h: answer.correctArea.height,
      }))
    const passages = seededReferences(row.id, version.stem)
    if (passages.length) values.references = passages
  }

  if (!values.rationale) {
    const rationale = showcaseItemRationale(version.stem)
    if (rationale) values.rationale = rationale
  }
  if (!values.references?.length) {
    const references = seededReferences(row.id, version.stem)
    if (references.length) values.references = references
  }

  return {
    questionId: row.id,
    questionTitle: row.title,
    versionNumber: version.number,
    type: authoringTypeForPoolType(row.type),
    folderId: row.folderId,
    folderIds: bankQuestionFolderIds(row),
    values,
  }
}

/**
 * The fields a saved composer form contributes back to a bank row.
 *
 * Structural rather than the composer's own `QuestionFormValues`, which is
 * private to that module: this is the contract between the two, and stating it
 * here keeps the form free to hold fields the bank has no column for.
 */
export interface QuestionAuthoringResult {
  type: AuthoringQuestionType
  title: string
  leadIn: string
  folderId: string
  /** All filing locations; when set, `folderId` should be the primary (first). */
  folderIds?: string[]
  options: { id: string; text: string; isCorrect: boolean; rationale: string }[]
  rationale: string
  difficulty: "easy" | "medium" | "hard"
  points: string
  bloom: string
}

const POOL_DIFFICULTY: Record<
  "easy" | "medium" | "hard",
  AssessmentBankQuestion["difficulty"]
> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
}

function revisionAnswer(values: QuestionAuthoringResult): QuestionAnswerModel {
  const written = values.options.filter((option) => option.text.trim())
  if (written.length === 0) {
    return { kind: "open", guidance: values.rationale }
  }
  return {
    kind: "choices",
    multiple: values.type === "mcq_multiple",
    choices: written.map((option, index) => ({
      letter: LETTERS[index] ?? String(index + 1),
      text: option.text,
      correct: option.isCorrect,
      // Nobody has sat this wording yet, so there is no split to report.
      share: null,
    })),
  }
}

/**
 * Folds a saved edit into the row as a new version.
 *
 * The row's own surface fields move with it — the grid has to show the wording
 * that is now current, or the pool and the panel would disagree about what the
 * question says. Everything the edit did not touch, including reuse history
 * and the id, is left exactly as it was: this is the same question.
 */
export function applyQuestionRevision(
  row: AssessmentBankQuestion,
  values: QuestionAuthoringResult,
  {
    author,
    topic,
    note = "Edited in this assessment",
    today = new Date(),
  }: {
    author: string
    /** Resolved by the surface, which owns the folder tree. */
    topic?: string
    note?: string
    today?: Date
  }
): AssessmentBankQuestion {
  const revision: QuestionRevision = {
    note,
    author,
    date: today.toISOString().slice(0, 10),
    stem: values.leadIn,
    answer: revisionAnswer(values),
  }

  return {
    ...row,
    title: values.title,
    stem: values.leadIn,
    type: poolTypeForAuthoringType(values.type),
    difficulty: POOL_DIFFICULTY[values.difficulty],
    points: Number(values.points) || row.points || 1,
    bloom: (values.bloom || row.bloom) as AssessmentBankQuestion["bloom"],
    folderId: values.folderId,
    folderIds: values.folderIds?.length
      ? values.folderIds
      : [values.folderId],
    topic: topic ?? row.topic,
    revisions: [...(row.revisions ?? []), revision],
  }
}
